import { Assets, Rectangle, Texture, TextureStyle } from 'pixi.js';
import { TILESET_COLS, TILE_PX } from './constants.js';

/**
 * Texture loading (TRD-03 §3).
 *
 * `scaleMode = 'nearest'` is set as the DEFAULT before anything loads. Setting it
 * per-texture after the fact means whichever texture slipped through gets bilinear
 * filtering, and a single blurred sprite in a pixel-art scene reads as a bug.
 */

const BASE = 'assets';

const SPRITE_NAMES = [
  'tower_base',
  'tower_ring',
  'tower_outline',
  'star_1',
  'star_2',
  'star_3',
  'proj_bullet',
  'proj_orb',
  'proj_shard',
  'shadow_8',
  'p_dot1',
  'p_dot2',
  'p_square3',
  'p_spark',
  'p_ring8',
  'p_shard4',
] as const;

const ENEMY_NAMES = [
  'enemy_runner',
  'enemy_brute',
  'enemy_sprinter',
  'enemy_ward',
  'enemy_splitter',
  'enemy_splitterling',
  'enemy_mender',
  'enemy_drifter',
  'boss_bulwark',
  'boss_hive',
  'boss_rust',
  'boss_chorus',
  'boss_prism',
] as const;

/**
 * Frames on the shared particle strip.
 *
 * All six live on one texture source so ParticleContainer can batch them into a single
 * draw call per blend mode (TRD-04 §10).
 */
const PARTICLE_FRAMES = {
  p_dot1: [0, 0, 1, 1],
  p_dot2: [8, 0, 2, 2],
  p_square3: [16, 0, 3, 3],
  p_spark: [24, 0, 1, 4],
  p_ring8: [32, 0, 8, 8],
  p_shard4: [40, 0, 4, 4],
} as const;

export type ParticleTextureName = keyof typeof PARTICLE_FRAMES;

export interface AssetBundle {
  sprite: Record<string, Texture>;
  enemy: Record<string, Texture>;
  /** White silhouettes for the damage flash (TRD-04 R-04-15). */
  enemyFlash: Record<string, Texture>;
  /** Tileset sliced into one texture per tile index. */
  tiles: Texture[];
  /** Particle frames, all sharing one source. */
  particle: Record<ParticleTextureName, Texture>;
}

let cached: AssetBundle | null = null;

export async function loadAssets(): Promise<AssetBundle> {
  if (cached) return cached;

  TextureStyle.defaultOptions.scaleMode = 'nearest';

  const paths = [
    ...SPRITE_NAMES.map((n) => `${BASE}/${n}.png`),
    ...ENEMY_NAMES.flatMap((n) => [`${BASE}/${n}.png`, `${BASE}/${n}_flash.png`]),
    `${BASE}/tiles.png`,
    `${BASE}/particles.png`,
  ];

  const loaded = (await Assets.load(paths)) as Record<string, Texture>;

  const sprite: Record<string, Texture> = {};
  for (const n of SPRITE_NAMES) sprite[n] = loaded[`${BASE}/${n}.png`] as Texture;

  const enemy: Record<string, Texture> = {};
  const enemyFlash: Record<string, Texture> = {};
  for (const n of ENEMY_NAMES) {
    enemy[n] = loaded[`${BASE}/${n}.png`] as Texture;
    enemyFlash[n] = loaded[`${BASE}/${n}_flash.png`] as Texture;
  }

  // Slice the tileset into per-index sub-textures once, at load. Doing it per draw would
  // allocate a Rectangle and a Texture every frame.
  const sheet = loaded[`${BASE}/tiles.png`] as Texture;
  const cols = TILESET_COLS;
  const rows = Math.floor(sheet.height / TILE_PX);
  const tiles: Texture[] = [];
  for (let i = 0; i < cols * rows; i++) {
    const x = (i % cols) * TILE_PX;
    const y = Math.floor(i / cols) * TILE_PX;
    tiles.push(
      new Texture({
        source: sheet.source,
        frame: new Rectangle(x, y, TILE_PX, TILE_PX),
      }),
    );
  }

  const strip = loaded[`${BASE}/particles.png`] as Texture;
  const particle = {} as Record<ParticleTextureName, Texture>;
  for (const [name, [x, y, w, h]] of Object.entries(PARTICLE_FRAMES)) {
    particle[name as ParticleTextureName] = new Texture({
      source: strip.source,
      frame: new Rectangle(x, y, w, h),
    });
  }

  cached = { sprite, enemy, enemyFlash, tiles, particle };
  return cached;
}

/** Enemy archetype id → sprite key. Falls back so unknown ids render rather than crash. */
export function enemyTextureKey(archetype: string): string {
  const direct = archetype.startsWith('boss-')
    ? `boss_${archetype.slice(5).replace(/-/g, '_')}`
    : `enemy_${archetype.replace(/-/g, '_')}`;
  return direct;
}
